import React from 'react';

// Compact phone-shaped frame used for all tutorial demos.
// All demos are pure CSS — no external assets, no video, infinite-loop friendly.
export const PhoneFrame = ({ children, height = 220 }) => (
  <div
    className="mx-auto rounded-[26px] overflow-hidden relative shadow-md"
    style={{
      width: 200,
      height,
      background: 'linear-gradient(180deg, #1a1a1f 0%, #0e0e12 100%)',
      border: '4px solid #25252b',
    }}
  >
    {/* Speaker dot */}
    <div className="absolute left-1/2 -translate-x-1/2 top-1.5 w-10 h-1 rounded-full bg-black/70 z-30" />
    {/* Inner screen */}
    <div className="absolute inset-x-1 top-4 bottom-1 rounded-[18px] overflow-hidden bg-white">
      {children}
    </div>
  </div>
);

/* -------------------- 1. LOGIN DEMO -------------------- */
export const LoginDemo = () => (
  <PhoneFrame height={240}>
    <style>{`
      @keyframes loginCursor {
        0%, 8%   { transform: translate(20px, 70px); opacity: 1; }
        14%, 22% { transform: translate(20px, 70px); opacity: 1; }
        28%, 42% { transform: translate(80px, 70px); opacity: 1; }
        48%, 58% { transform: translate(80px, 110px); opacity: 1; }
        64%, 80% { transform: translate(82px, 175px); opacity: 1; }
        86%, 100%{ transform: translate(82px, 175px); opacity: 0; }
      }
      @keyframes phoneFill   { 0%,28%{width:0} 42%,100%{width:80px} }
      @keyframes pkFill      { 0%,48%{width:0} 60%,100%{width:90px} }
      @keyframes btnPulse    { 0%,64%{box-shadow:0 0 0 0 rgba(217,138,148,0)} 70%{box-shadow:0 0 0 10px rgba(217,138,148,0.4)} 78%,100%{box-shadow:0 0 0 0 rgba(217,138,148,0)} }
      @keyframes confirmShow { 0%,72%{opacity:0;transform:scale(.9)} 80%,100%{opacity:1;transform:scale(1)} }
    `}</style>
    <div className="relative w-full h-full text-[8px] p-2.5">
      <div className="text-center font-bold mb-2 text-[#D98A94]">LOGIN</div>

      {/* WhatsApp row */}
      <div className="mb-1.5">
        <div className="text-[7px] text-gray-500 mb-0.5">WhatsApp</div>
        <div className="flex gap-1">
          <div className="px-1 py-1 rounded bg-gray-100 text-[8px] font-medium">+62 ▾</div>
          <div className="flex-1 px-1.5 py-1 rounded bg-gray-100 relative">
            <div
              className="h-2 bg-[#0EA5E9]/30 rounded"
              style={{ animation: 'phoneFill 4s ease-in-out infinite' }}
            />
          </div>
        </div>
      </div>

      {/* Passkey */}
      <div className="mb-2">
        <div className="text-[7px] text-gray-500 mb-0.5">Passkey</div>
        <div className="px-1.5 py-1 rounded bg-gray-100">
          <div className="h-2 bg-[#C7867A]/30 rounded" style={{ animation: 'pkFill 4s ease-in-out infinite' }} />
        </div>
      </div>

      {/* Login button */}
      <div
        className="text-center py-1.5 rounded-full bg-[#D98A94] text-white font-bold text-[8px] tracking-wide"
        style={{ animation: 'btnPulse 4s ease-in-out infinite' }}
      >
        ♥ LOGIN
      </div>

      {/* Confirmation popup */}
      <div
        className="absolute left-2 right-2 top-12 bg-white border-2 border-[#25D366]/40 rounded-lg p-2 shadow-lg"
        style={{ animation: 'confirmShow 4s ease-in-out infinite' }}
      >
        <div className="text-[7px] text-gray-500 text-center">Confirm number</div>
        <div className="text-center font-bold text-[11px] text-gray-800 my-1">+62 812···7890</div>
        <div className="flex gap-1">
          <div className="flex-1 text-center py-1 rounded-full border text-[7px]">Edit</div>
          <div className="flex-1 text-center py-1 rounded-full bg-[#D98A94] text-white text-[7px]">Yes ✓</div>
        </div>
      </div>

      {/* Animated cursor */}
      <div
        className="absolute w-3.5 h-3.5 pointer-events-none"
        style={{ animation: 'loginCursor 4s ease-in-out infinite', left: 0, top: 0 }}
      >
        <div className="w-3.5 h-3.5 rounded-full bg-[#D98A94]/50 ring-2 ring-[#D98A94]" />
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- 2. FILTER DEMO -------------------- */
export const FilterDemo = () => (
  <PhoneFrame height={240}>
    <style>{`
      @keyframes filterCursor {
        0%, 10%   { transform: translate(150px, 18px); opacity: 1; }
        16%, 26%  { transform: translate(150px, 18px); opacity: 1; }
        32%, 50%  { transform: translate(90px, 75px);  opacity: 1; }
        56%, 72%  { transform: translate(90px, 110px); opacity: 1; }
        78%, 100% { transform: translate(90px, 145px); opacity: 1; }
      }
      @keyframes panelShow   { 0%,16%{opacity:0;transform:translateY(-5px)} 22%,100%{opacity:1;transform:translateY(0)} }
      @keyframes dropdown1   { 0%,32%{background:rgb(243,244,246)} 40%,50%{background:rgba(217,138,148,0.15)} 60%,100%{background:rgb(243,244,246)} }
      @keyframes dropdown2   { 0%,56%{background:rgb(243,244,246)} 64%,72%{background:rgba(217,138,148,0.15)} 82%,100%{background:rgb(243,244,246)} }
      @keyframes ageHl       { 0%,78%{background:rgb(243,244,246)} 86%,100%{background:rgba(217,138,148,0.15)} }
    `}</style>
    <div className="relative w-full h-full p-2 text-[7px]">
      {/* Header row */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-bold text-[#D98A94] text-[9px]">Browse</div>
        <div className="px-1.5 py-0.5 rounded-full bg-gray-100 flex items-center gap-1 text-[7px]">
          <span>⚙</span> Filter
        </div>
      </div>

      {/* Filter panel */}
      <div
        className="bg-white rounded-lg border border-gray-200 p-1.5 mb-1 space-y-1"
        style={{ animation: 'panelShow 4s ease-in-out infinite' }}
      >
        <div className="text-[6px] text-gray-500">Domicile</div>
        <div className="rounded px-1 py-0.5" style={{ animation: 'dropdown1 4s ease-in-out infinite' }}>Jakarta ▾</div>
        <div className="text-[6px] text-gray-500">Category</div>
        <div className="rounded px-1 py-0.5" style={{ animation: 'dropdown2 4s ease-in-out infinite' }}>Buddhist Match ▾</div>
        <div className="text-[6px] text-gray-500">Age</div>
        <div className="flex gap-1">
          <div className="flex-1 rounded px-1 py-0.5 text-center" style={{ animation: 'ageHl 4s ease-in-out infinite' }}>25</div>
          <div className="flex-1 rounded px-1 py-0.5 text-center" style={{ animation: 'ageHl 4s ease-in-out infinite', animationDelay: '0.1s' }}>35</div>
        </div>
      </div>

      {/* Cursor */}
      <div className="absolute w-3.5 h-3.5 pointer-events-none" style={{ animation: 'filterCursor 4s ease-in-out infinite' }}>
        <div className="w-3.5 h-3.5 rounded-full bg-[#D98A94]/50 ring-2 ring-[#D98A94]" />
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- 3. VIEW PROFILE DEMO -------------------- */
export const ViewDemo = () => (
  <PhoneFrame height={240}>
    <style>{`
      @keyframes viewCursor {
        0%, 10%   { transform: translate(45px, 70px); opacity: 1; }
        16%, 30%  { transform: translate(45px, 70px); opacity: 1; }
        36%, 55%  { transform: translate(95px, 100px); opacity: 1; }
        61%, 80%  { transform: translate(165px, 25px); opacity: 1; }
        86%, 100% { transform: translate(165px, 25px); opacity: 0; }
      }
      @keyframes modalShow  { 0%,16%{opacity:0;transform:scale(.85)} 22%,82%{opacity:1;transform:scale(1)} 90%,100%{opacity:0} }
      @keyframes photoSwap  { 0%,36%{background:#FECDD3} 42%,55%{background:#A7F3D0} 61%,80%{background:#BFDBFE} }
      @keyframes navHl      { 0%,55%{transform:scale(1)} 62%,72%{transform:scale(1.15)} 80%,100%{transform:scale(1)} }
    `}</style>
    <div className="relative w-full h-full p-2 text-[7px]">
      {/* Card grid bg (faded when modal open) */}
      <div className="grid grid-cols-2 gap-1 opacity-25">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="aspect-[3/4] rounded bg-gray-200" />
        ))}
      </div>

      {/* Modal */}
      <div
        className="absolute inset-2 rounded-lg bg-white border border-gray-200 overflow-hidden shadow-lg"
        style={{ animation: 'modalShow 4s ease-in-out infinite' }}
      >
        {/* Close button — bigger */}
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/55 text-white flex items-center justify-center text-[9px] font-bold z-10" style={{ animation: 'navHl 4s ease-in-out infinite' }}>×</div>

        {/* Photo */}
        <div className="w-full h-32 relative" style={{ animation: 'photoSwap 4s ease-in-out infinite' }}>
          {/* Photo carousel dots */}
          <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-0.5">
            <div className="w-1 h-1 rounded-full bg-white/80" />
            <div className="w-3 h-1 rounded-full bg-white" />
            <div className="w-1 h-1 rounded-full bg-white/80" />
          </div>
          <div className="absolute bottom-1 left-1 text-[8px] text-white font-bold drop-shadow">Lina, 28</div>
        </div>

        {/* Quick info */}
        <div className="p-1.5 space-y-0.5">
          <div className="h-1 bg-gray-200 rounded w-3/4" />
          <div className="h-1 bg-gray-200 rounded w-1/2" />
          <div className="flex gap-1 mt-1">
            <div className="flex-1 py-0.5 rounded-full bg-[#25D366]/15 text-[#25D366] text-center text-[6px]">WA</div>
            <div className="flex-1 py-0.5 rounded-full bg-[#E1306C]/15 text-[#E1306C] text-center text-[6px]">IG</div>
          </div>
        </div>
      </div>

      {/* Cursor */}
      <div className="absolute w-3.5 h-3.5 pointer-events-none" style={{ animation: 'viewCursor 4s ease-in-out infinite' }}>
        <div className="w-3.5 h-3.5 rounded-full bg-[#D98A94]/50 ring-2 ring-[#D98A94]" />
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- 4. CLAIM DEMO -------------------- */
export const ClaimDemo = () => (
  <PhoneFrame height={240}>
    <style>{`
      @keyframes claimCursor {
        0%, 10%   { transform: translate(85px, 30px); opacity: 1; }
        16%, 28%  { transform: translate(85px, 30px); opacity: 1; }
        34%, 68%  { transform: translate(85px, 90px); opacity: 1; }
        74%, 92%  { transform: translate(85px, 145px); opacity: 1; }
      }
      @keyframes claimBtnHl { 0%,16%{box-shadow:0 0 0 0 rgba(217,138,148,0)} 22%{box-shadow:0 0 0 8px rgba(217,138,148,0.4)} 28%,100%{box-shadow:none} }
      @keyframes progress   { 0%,28%{width:0} 60%{width:100%} 100%{width:100%} }
      @keyframes searchFill { 0%,60%{width:0} 70%,100%{width:60px} }
      @keyframes listShow   { 0%,60%{opacity:0} 70%,100%{opacity:1} }
      @keyframes itemHl     { 0%,74%{background:white} 82%,100%{background:rgba(217,138,148,0.12);border-color:#D98A94} }
    `}</style>
    <div className="relative w-full h-full p-2 text-[7px]">
      <div className="font-bold text-[#D98A94] text-[9px] mb-1.5">Claim Profile</div>

      {/* "Claim Profile" button */}
      <div
        className="rounded-full bg-[#D98A94] text-white text-center py-1 font-bold text-[8px] mb-2"
        style={{ animation: 'claimBtnHl 5s ease-in-out infinite' }}
      >
        ✓ Claim Profile
      </div>

      {/* Progress bar */}
      <div className="mb-1.5">
        <div className="flex justify-between text-[6px] text-gray-500 mb-0.5">
          <span>Loading profiles…</span>
          <span>100%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#D98A94] to-[#C7867A]"
            style={{ animation: 'progress 5s ease-in-out infinite' }}
          />
        </div>
      </div>

      {/* Search input */}
      <div className="bg-gray-100 rounded px-1.5 py-1 mb-1 text-[6px] text-gray-400 relative" style={{ animation: 'listShow 5s ease-in-out infinite' }}>
        🔍 <span className="ml-0.5">WhatsApp...</span>
        <div className="absolute left-5 top-1 h-1 bg-gray-700 rounded" style={{ animation: 'searchFill 5s ease-in-out infinite' }} />
      </div>

      {/* List */}
      <div className="space-y-1" style={{ animation: 'listShow 5s ease-in-out infinite' }}>
        <div className="rounded p-1 border border-gray-200 flex items-center gap-1" style={{ animation: 'itemHl 5s ease-in-out infinite' }}>
          <div className="w-3 h-3 rounded bg-gray-300" />
          <div className="flex-1">
            <div className="text-[6px] font-bold">Lina K.</div>
            <div className="text-[5px] text-gray-500">+6281234···</div>
          </div>
          <span className="text-[#D98A94]">✓</span>
        </div>
      </div>

      {/* Cursor */}
      <div className="absolute w-3.5 h-3.5 pointer-events-none" style={{ animation: 'claimCursor 5s ease-in-out infinite' }}>
        <div className="w-3.5 h-3.5 rounded-full bg-[#D98A94]/50 ring-2 ring-[#D98A94]" />
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- 5. EDIT PROFILE DEMO -------------------- */
export const EditDemo = () => (
  <PhoneFrame height={240}>
    <style>{`
      @keyframes editCursor {
        0%, 12%   { transform: translate(150px, 14px); opacity: 1; }
        18%, 32%  { transform: translate(150px, 14px); opacity: 1; }
        38%, 58%  { transform: translate(50px, 80px);  opacity: 1; }
        64%, 78%  { transform: translate(90px, 130px); opacity: 1; }
        84%, 100% { transform: translate(90px, 195px); opacity: 1; }
      }
      @keyframes editFormShow { 0%,18%{opacity:0;transform:scale(.95)} 24%,100%{opacity:1;transform:scale(1)} }
      @keyframes camHl    { 0%,38%{transform:scale(1);background:#F3F4F6} 46%{transform:scale(1.2);background:rgba(199,134,122,0.2)} 58%,100%{transform:scale(1);background:#F3F4F6} }
      @keyframes fieldFill{ 0%,64%{width:0} 76%,100%{width:70%} }
      @keyframes saveHl   { 0%,84%{box-shadow:0 0 0 0 rgba(217,138,148,0)} 92%{box-shadow:0 0 0 8px rgba(217,138,148,0.4)} 100%{box-shadow:none} }
    `}</style>
    <div className="relative w-full h-full p-2 text-[7px]">
      <div className="flex items-center justify-between mb-1">
        <div className="font-bold text-[#D98A94] text-[9px]">Profile</div>
        <div className="px-1.5 py-0.5 rounded-full bg-[#C7867A] text-white text-[7px]">✎ Edit</div>
      </div>

      <div
        className="bg-white border border-gray-200 rounded-lg p-1.5 space-y-1.5"
        style={{ animation: 'editFormShow 5s ease-in-out infinite' }}
      >
        {/* Photo slots */}
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="aspect-square rounded relative" style={{ flex: 1, animation: i === 0 ? 'camHl 5s ease-in-out infinite' : 'none', background: '#F3F4F6' }}>
              <div className="absolute inset-0 flex items-center justify-center text-[8px]">📷</div>
            </div>
          ))}
        </div>
        {/* Bio fields */}
        <div className="space-y-1">
          <div className="text-[6px] text-gray-500">Bio</div>
          <div className="h-2 bg-gray-100 rounded relative overflow-hidden">
            <div className="h-full bg-gray-400 rounded" style={{ animation: 'fieldFill 5s ease-in-out infinite' }} />
          </div>
          <div className="text-[6px] text-gray-500">Hobby</div>
          <div className="h-2 bg-gray-100 rounded relative overflow-hidden">
            <div className="h-full bg-gray-400 rounded" style={{ animation: 'fieldFill 5s ease-in-out infinite', animationDelay: '0.2s' }} />
          </div>
        </div>
        {/* Save */}
        <div className="text-center py-1 rounded-full bg-[#D98A94] text-white text-[7px] font-bold" style={{ animation: 'saveHl 5s ease-in-out infinite' }}>SAVE</div>
      </div>

      {/* Cursor */}
      <div className="absolute w-3.5 h-3.5 pointer-events-none" style={{ animation: 'editCursor 5s ease-in-out infinite' }}>
        <div className="w-3.5 h-3.5 rounded-full bg-[#D98A94]/50 ring-2 ring-[#D98A94]" />
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- 6. LIKE DEMO -------------------- */
export const LikeDemo = () => (
  <PhoneFrame height={240}>
    <style>{`
      @keyframes likeCursor {
        0%, 15%   { transform: translate(40px, 110px); opacity: 1; }
        21%, 45%  { transform: translate(40px, 110px); opacity: 1; }
        51%, 100% { transform: translate(120px, 150px); opacity: 1; }
      }
      @keyframes heartPulse { 0%,15%{transform:scale(1)} 24%{transform:scale(1.3)} 35%,100%{transform:scale(1)} }
      @keyframes heartFill  { 0%,15%{color:#9CA3AF;fill:transparent} 22%,100%{color:#D98A94;fill:#D98A94} }
      @keyframes waHl       { 0%,51%{box-shadow:0 0 0 0 rgba(37,211,102,0)} 60%{box-shadow:0 0 0 8px rgba(37,211,102,0.4)} 70%,100%{box-shadow:none} }
      @keyframes countUp    { 0%,15%{content:'12'} 24%,100%{content:'13'} }
    `}</style>
    <div className="relative w-full h-full p-2 text-[7px]">
      <div className="text-center text-[7px] text-gray-500 mb-1.5">Member popup</div>
      <div className="bg-gray-100 rounded mb-2 aspect-[4/3] relative">
        <div className="absolute bottom-1 left-1 text-[8px] text-white font-bold drop-shadow">Lina, 28</div>
      </div>

      {/* Like row */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className="text-[14px]"
          style={{ animation: 'heartPulse 3.5s ease-in-out infinite, heartFill 3.5s ease-in-out infinite' }}
        >
          ♥
        </div>
        <div className="text-[7px] text-gray-700">12 likes</div>
      </div>

      {/* Social buttons */}
      <div className="flex gap-1 mb-1.5">
        <div
          className="flex-1 py-1 rounded-full bg-[#25D366]/15 text-[#25D366] text-center text-[7px] font-bold"
          style={{ animation: 'waHl 3.5s ease-in-out infinite' }}
        >
          WhatsApp
        </div>
        <div className="flex-1 py-1 rounded-full bg-[#E1306C]/15 text-[#E1306C] text-center text-[7px]">Instagram</div>
      </div>

      <div className="text-[6px] text-amber-600 bg-amber-50 rounded p-1 leading-tight">
        ⚠ Empty page = wrong username — just move on!
      </div>

      <div className="absolute w-3.5 h-3.5 pointer-events-none" style={{ animation: 'likeCursor 3.5s ease-in-out infinite' }}>
        <div className="w-3.5 h-3.5 rounded-full bg-[#D98A94]/50 ring-2 ring-[#D98A94]" />
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- 7. BROWSE DEMO -------------------- */
export const BrowseDemo = () => (
  <PhoneFrame height={240}>
    <style>{`
      @keyframes browseScroll { 0%,30%{transform:translateY(0)} 50%,80%{transform:translateY(-40px)} 100%{transform:translateY(-40px)} }
      @keyframes cardTap      { 0%,30%{box-shadow:0 0 0 0 rgba(217,138,148,0)} 38%{box-shadow:0 0 0 4px rgba(217,138,148,0.5)} 50%,100%{box-shadow:0 0 0 0 rgba(217,138,148,0)} }
    `}</style>
    <div className="relative w-full h-full p-2 text-[7px] overflow-hidden">
      {/* Top section: leaderboard */}
      <div className="text-[6px] font-bold text-[#F59E0B] mb-0.5">👑 Top Liked This Month</div>
      <div className="flex gap-1 mb-1.5">
        {['#F59E0B', '#94A3B8', '#B45309'].map((c, i) => (
          <div key={c} className="flex-1 flex items-center gap-0.5 py-0.5 px-1 rounded-full glass text-[6px]" style={{ border: `1px solid ${c}55` }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
            <span>{i + 1}</span>
          </div>
        ))}
      </div>

      <div className="text-[6px] font-bold text-[#D98A94] mb-1">✨ Suitable for You</div>

      {/* Scrolling grid */}
      <div style={{ animation: 'browseScroll 3.5s ease-in-out infinite' }}>
        <div className="grid grid-cols-2 gap-1 mb-1">
          <div className="aspect-[3/4] rounded bg-gradient-to-br from-pink-200 to-rose-200" />
          <div className="aspect-[3/4] rounded bg-gradient-to-br from-blue-200 to-sky-200" style={{ animation: 'cardTap 3.5s ease-in-out infinite' }} />
        </div>
        <div className="grid grid-cols-2 gap-1 mb-1">
          <div className="aspect-[3/4] rounded bg-gradient-to-br from-amber-200 to-orange-200" />
          <div className="aspect-[3/4] rounded bg-gradient-to-br from-emerald-200 to-teal-200" />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <div className="aspect-[3/4] rounded bg-gradient-to-br from-violet-200 to-purple-200" />
          <div className="aspect-[3/4] rounded bg-gradient-to-br from-fuchsia-200 to-pink-200" />
        </div>
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- 8. BOTTOM NAV DEMO -------------------- */
export const NavDemo = () => (
  <PhoneFrame height={170}>
    <style>{`
      @keyframes navCycle1 { 0%,28%{color:#D98A94;transform:scale(1.15)} 33%,100%{color:#9CA3AF;transform:scale(1)} }
      @keyframes navCycle2 { 0%,33%{color:#9CA3AF;transform:scale(1)} 38%,61%{color:#D98A94;transform:scale(1.15)} 66%,100%{color:#9CA3AF;transform:scale(1)} }
      @keyframes navCycle3 { 0%,66%{color:#9CA3AF;transform:scale(1)} 72%,100%{color:#D98A94;transform:scale(1.15)} }
    `}</style>
    <div className="relative w-full h-full p-2 flex flex-col">
      <div className="flex-1 flex items-center justify-center text-[7px] text-gray-400">App content here</div>
      <div className="flex items-center justify-around py-2 border-t border-gray-200">
        <div className="flex flex-col items-center gap-0.5" style={{ animation: 'navCycle1 3s ease-in-out infinite' }}>
          <div className="text-[14px]">🔍</div>
          <div className="text-[6px] font-bold">Browse</div>
        </div>
        <div className="flex flex-col items-center gap-0.5" style={{ animation: 'navCycle2 3s ease-in-out infinite' }}>
          <div className="text-[14px]">♥</div>
          <div className="text-[6px] font-bold">Liked</div>
        </div>
        <div className="flex flex-col items-center gap-0.5" style={{ animation: 'navCycle3 3s ease-in-out infinite' }}>
          <div className="text-[14px]">👤</div>
          <div className="text-[6px] font-bold">Profile</div>
        </div>
      </div>
    </div>
  </PhoneFrame>
);

/* -------------------- DemoSection wrapper -------------------- */
export const DemoSection = ({ children, label }) => (
  <div className="ml-12 mt-4 mb-2">
    <div className="t-text-muted text-[10px] uppercase tracking-wider mb-2 font-semibold">{label}</div>
    <div className="flex justify-center py-3 rounded-2xl glass-card">{children}</div>
  </div>
);
